import { getInitialDetachParams } from "components/DocumentDetachOptions";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";
import { modalTypes } from "constants/types";
import { useDispatch, useSelector, useStore } from "react-redux";
import { hideModal, showModal } from "slices/app/actions/modals";
import { fetchOutlineCounts } from "slices/comments";
import {
  deleteProposalOutlineByOwner,
  fetchProposalOutlineByOwner,
} from "slices/proposalOutline/actions/outlineActions";
import { PROPOSAL_OUTLINE_OWNER_TYPES } from "slices/proposalOutline/constants";
import {
  selectOutlineId,
  selectOutlineOwner,
  selectProposalOutlineSections,
} from "slices/proposalOutline/selectors";
import { getDocumentList } from "slices/writing/documents/actions";
import { pluralize } from "utils/string";

const OUTLINE_SCOPE =
  "By deleting, we will remove this outline in total, along with all data.";

const useDeleteOutline = () => {
  const dispatch = useDispatch();
  const owner = useSelector(selectOutlineOwner);
  const outlineId = useSelector(selectOutlineId);
  const store = useStore();

  const onConfirm = async ({ deleteDocument, detachParams } = {}) => {
    const { ownerId, ownerType } = owner;
    const params = { deleteDocument, detachParams };
    const entityType =
      ownerType === PROPOSAL_OUTLINE_OWNER_TYPES.MISC_ANALYSIS
        ? UPLOAD_ENTITY_TYPES.MISC_ANALYSIS_PROPOSAL_WRITING
        : ownerType;

    dispatch(hideModal(modalTypes.DELETE_OUTLINE_MODAL));

    await dispatch(deleteProposalOutlineByOwner(ownerType, ownerId, params));
    await dispatch(getDocumentList(ownerId, entityType));
  };

  return async () => {
    if (!owner) {
      return;
    }

    const { ownerId, ownerType } = owner;
    const data = await dispatch(
      fetchProposalOutlineByOwner(ownerType, ownerId, true)
    );

    if (data?.locked && !data?.lockedByCurrentUser) {
      return;
    }

    const rsp = await dispatch(fetchOutlineCounts({ outlineId }));
    const totalComments = rsp?.data?.totalComments || 0;

    const noun = pluralize("comment", totalComments);
    const caveat =
      totalComments > 0
        ? ` This will also delete ${totalComments} ${noun} left on its sections.`
        : "";

    // Read the sections after the refetch above, so the copy matches the linked documents
    // the modal is about to offer options for.
    const sections = selectProposalOutlineSections(store.getState());
    const hasLinkedDocuments =
      Object.keys(getInitialDetachParams(sections)).length > 0;
    const documentChoice = hasLinkedDocuments
      ? " Choose below what should happen to the documents linked to its sections."
      : "";

    const bodyText =
      OUTLINE_SCOPE +
      caveat +
      " This cannot be undone. Do you still want to delete?" +
      documentChoice;

    dispatch(
      showModal(modalTypes.DELETE_OUTLINE_MODAL, { bodyText, onConfirm })
    );
  };
};

export default useDeleteOutline;
